import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  LinearProgress,
  Paper,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import EurCurrencyField from '@/components/form/EurCurrencyField';

export interface CoInvestorFormRow {
  email: string;
  contributionAmount: number | null;
  ownershipPercentage: number | null;
  isPaid: boolean;
  feeType: 'FIXED' | 'PERCENTAGE' | '';
  feeAmount: number | null;
  feePercentage: number | null;
}

interface CoInvestorsStepProps {
  coInvestors: CoInvestorFormRow[];
  onChange: (coInvestors: CoInvestorFormRow[]) => void;
  acquisitionPrice: number;
  disabled?: boolean;
}

const emptyRow: CoInvestorFormRow = {
  email: '',
  contributionAmount: null,
  ownershipPercentage: null,
  isPaid: false,
  feeType: '',
  feeAmount: null,
  feePercentage: null,
};

export const CoInvestorsStep = ({
  coInvestors,
  onChange,
  acquisitionPrice,
  disabled = false,
}: CoInvestorsStepProps) => {
  const totalAmount = coInvestors.reduce((sum, ci) => sum + (ci.contributionAmount ?? 0), 0);
  const totalRate = coInvestors.reduce((sum, ci) => sum + (ci.ownershipPercentage ?? 0), 0);
  const totalPercentage = totalRate * 100;
  const progressValue =
    acquisitionPrice > 0 ? Math.min((totalAmount / acquisitionPrice) * 100, 100) : 0;
  const isValid = acquisitionPrice > 0 && Math.abs(totalAmount - acquisitionPrice) <= 1;

  const handleAdd = () => {
    onChange([...coInvestors, { ...emptyRow }]);
  };

  const handleRemove = (index: number) => {
    onChange(coInvestors.filter((_, i) => i !== index));
  };

  const handleEmailChange = (index: number, value: string) => {
    const updated = [...coInvestors];
    updated[index] = { ...updated[index], email: value };
    onChange(updated);
  };

  const handleAmountChange = (index: number, value: number | null) => {
    const updated = [...coInvestors];
    const ownershipPercentage =
      acquisitionPrice > 0 && value != null ? value / acquisitionPrice : null;
    updated[index] = { ...updated[index], contributionAmount: value, ownershipPercentage };
    onChange(updated);
  };

  const formatPercentage = (rate: number | null): string => {
    if (rate == null) return '—';
    return `${(rate * 100).toFixed(2)}%`;
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Coinversores
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Agrega los coinversores de esta inversión. La suma de los importes debe igualar el precio de
        adquisición.
      </Typography>

      {/* Progress bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" fontWeight={500}>
            Participación total asignada
          </Typography>
          <Chip
            label={`${totalPercentage.toFixed(2)}%`}
            color={isValid ? 'success' : 'warning'}
            size="small"
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          color={isValid ? 'success' : totalAmount > acquisitionPrice ? 'error' : 'warning'}
          sx={{ height: 8, borderRadius: 4 }}
        />
        {!isValid && acquisitionPrice > 0 && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            {totalAmount > acquisitionPrice
              ? `La suma supera el precio de adquisición`
              : `Falta ${formatPercentage(1 - totalRate)} por asignar`}
          </Typography>
        )}
      </Paper>

      {/* Table */}
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 36, pl: 0, color: 'text.secondary', fontWeight: 500 }}>
              #
            </TableCell>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Email del inversor
            </TableCell>
            <TableCell sx={{ width: 200, color: 'text.secondary', fontWeight: 500 }}>
              Importe
            </TableCell>
            <TableCell
              align="center"
              sx={{ width: 90, color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              % Participación
            </TableCell>
            <TableCell sx={{ width: 36, pr: 0 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {coInvestors.map((ci, index) => (
            <TableRow key={index} sx={{ '& td': { py: 1, verticalAlign: 'middle' } }}>
              <TableCell sx={{ pl: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {index + 1}
                </Typography>
              </TableCell>
              <TableCell>
                <TextField
                  disabled={disabled}
                  inputProps={{ 'aria-label': `Email del coinversor ${index + 1}` }}
                  type="email"
                  placeholder="inversor@email.com"
                  value={ci.email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  size="small"
                  fullWidth
                  required
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <EurCurrencyField
                  disabled={disabled}
                  aria-label={`Importe del coinversor ${index + 1}`}
                  placeholder="0,00 €"
                  value={ci.contributionAmount}
                  onChange={(val) => handleAmountChange(index, val)}
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={formatPercentage(ci.ownershipPercentage)}
                  size="small"
                  variant="outlined"
                  color={
                    ci.ownershipPercentage != null && ci.ownershipPercentage > 0
                      ? 'primary'
                      : 'default'
                  }
                />
              </TableCell>
              <TableCell sx={{ pr: 0 }}>
                {coInvestors.length > 2 && (
                  <Tooltip title="Eliminar">
                    <IconButton
                      disabled={disabled}
                      aria-label={`Eliminar coinversor ${index + 1}`}
                      size="small"
                      color="error"
                      onClick={() => handleRemove(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Button
        disabled={disabled}
        startIcon={<AddIcon />}
        onClick={handleAdd}
        variant="outlined"
        size="small"
      >
        Agregar coinversor
      </Button>
    </Box>
  );
};
